(() => {
  'use strict';

  if (window.__PMP_I18N_DOM_ENGINE__) return;
  window.__PMP_I18N_DOM_ENGINE__ = true;

  const boot = () => {
    const core = window.PMPI18nCore;
    const payload = window.PMP_I18N_PAYLOAD;

    if (!core || !payload) {
      setTimeout(boot, 50);
      return;
    }

    const runtime = core.create(payload);
    if (!runtime) return;

    const {
      locale,
      rows,
      dictionary,
      knownTranslations,
      normalize,
      translate
    } = runtime;

    const blockedTags = new Set([
      'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'TEXTAREA', 'CODE', 'PRE', 'IFRAME'
    ]);

    const translatedAttributes = [
      'aria-label',
      'aria-description',
      'title',
      'placeholder',
      'alt',
      'data-label',
      'data-text',
      'data-title',
      'data-note',
      'data-empty-text',
      'data-loading-text',
      'data-error',
      'data-success-text',
      'data-pmp-sticky-tier-one',
      'data-pmp-sticky-tier-two',
      'data-pmp-sticky-tier-three',
      'data-pmp-sticky-add',
      'data-pmp-sticky-shipping'
    ];

    const isBlocked = (element) =>
      !element ||
      element.nodeType !== Node.ELEMENT_NODE ||
      blockedTags.has(element.tagName) ||
      Boolean(element.closest(
        '[data-pmp-i18n-ignore], [data-pmp-i18n-skip], [translate="no"], [contenteditable="true"]'
      ));

    const translateTextNode = (node) => {
      if (
        !node ||
        node.nodeType !== Node.TEXT_NODE ||
        !node.parentElement ||
        isBlocked(node.parentElement)
      ) return;

      const original = node.nodeValue;
      const translated = translate(original);
      if (translated !== original) node.nodeValue = translated;
    };

    const translateElement = (element) => {
      if (isBlocked(element)) return;

      for (const attribute of translatedAttributes) {
        if (!element.hasAttribute(attribute)) continue;

        const original = element.getAttribute(attribute);
        const translated = translate(original);
        if (translated !== original) element.setAttribute(attribute, translated);
      }

      if (element.shadowRoot) translateTree(element.shadowRoot);
    };

    const translateTree = (root) => {
      if (!root) return;

      if (root.nodeType === Node.TEXT_NODE) {
        translateTextNode(root);
        return;
      }

      if (
        root.nodeType !== Node.ELEMENT_NODE &&
        root.nodeType !== Node.DOCUMENT_NODE &&
        root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE
      ) return;

      if (root.nodeType === Node.ELEMENT_NODE && isBlocked(root)) return;
      if (root.nodeType === Node.ELEMENT_NODE) translateElement(root);

      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            if (node.nodeType === Node.ELEMENT_NODE && isBlocked(node)) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) translateTextNode(node);
        else translateElement(node);
      }
    };

    const pending = new Set();
    let scheduled = false;

    const schedule = (node) => {
      if (!node) return;

      pending.add(node);
      if (scheduled) return;
      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        const batch = [...pending];
        pending.clear();
        for (const item of batch) translateTree(item);
      });
    };

    const collectMissing = (root = document.body) => {
      if (!root) return [];

      const missing = new Set();

      const inspect = (value) => {
        const text = normalize(value);
        if (
          text.length < 3 ||
          knownTranslations.has(text) ||
          /^[\d\s.,%+\-–—×/$€£¥CADUSEURGBP]+$/i.test(text) ||
          /^(https?:|mailto:|tel:)/i.test(text)
        ) return;

        if (
          dictionary.has(text) ||
          /^Translation missing:/i.test(text) ||
          /\b(the|and|or|for|with|from|your|you|shop|buy|add|cart|order|shipping|free|product|products|collection|collections|learn|view|select|quantity|search|close|continue|support|guide|routine|formula|checkout|subtotal|total|remove|loading)\b/i.test(text)
        ) {
          missing.add(text);
        }
      };

      const walker = document.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) {
          const parent = node.parentElement;
          if (parent && !isBlocked(parent)) inspect(node.nodeValue);
        } else if (node.nodeType === Node.ELEMENT_NODE && !isBlocked(node)) {
          for (const attribute of translatedAttributes) {
            if (node.hasAttribute(attribute)) inspect(node.getAttribute(attribute));
          }
        }
      }

      return [...missing].sort((a, b) => a.localeCompare(b));
    };

    const start = () => {
      if (!document.body) return;

      translateTree(document.body);

      const observer = new MutationObserver((records) => {
        for (const record of records) {
          if (record.type === 'childList') {
            for (const node of record.addedNodes) schedule(node);
          } else {
            schedule(record.target);
          }
        }
      });

      observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: translatedAttributes
      });

      [
        'shopify:section:load',
        'shopify:section:reorder',
        'cart:update',
        'cart:updated',
        'cart:change',
        'cart:refresh',
        'product:variant-change',
        'variant:change',
        'pmp:cart-updated',
        'pmp:upsell-opened'
      ].forEach((eventName) => {
        document.addEventListener(eventName, () => schedule(document.body));
      });

      [150, 500, 1200, 2500, 5000, 9000].forEach((delay) => {
        setTimeout(() => translateTree(document.body), delay);
      });

      window.PMPI18n = Object.assign(window.PMPI18n || {}, {
        locale,
        sourceCount: rows.length,
        translate,
        scan: translateTree,
        getMissing: collectMissing
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  };

  boot();
})();
